import React, { useEffect, useState } from "react";
import logo from "../../assets/img/spire360-by-wellinks-logo.png";
import { Spinner, Toast } from "react-bootstrap";
import { Modal } from "react-bootstrap";
import "../../assets/scss/custom/_carePlan.scss";
import {
  createCarePlanApi,
  createPatientApi,
} from "../../api-helpers/PatientApiHelper";
import {
  CarePlanAddendumDto,
  CarePlanDto,
  CarePlanReviewRequestDto,
  Contracts,
  GridDataResponse,
  InternalCareTeamMembers,
  PatientPersonalInfo,
  UpdateCarePlanRequestDto,
} from "patients-experience-client";
import { formatDate, formatDateTime, getAge } from "../../helper/dateFormatter";
import {
  createCommonUtilsApi,
  createWorkflowApi,
} from "../../api-helpers/CommonUtilsApiHelper";
import { Lookup } from "../../interfaces/allergy";
import Lookups, { BILLING_MODELS } from "../../constants/Lookups";
import { createPgiApi } from "../../api-helpers/PgiApiHelper";
import ReactQuill from "react-quill";
import { WorkflowState } from "common-util-experience-client";
import {
  WorkflowConstants,
  WorkflowStateConstants,
} from "../../helper/WorkflowConstants";
import { LoadingHelper } from "../../components/loader/LoadingHelper";
import { createGridApi } from "../../api-helpers/GridApiHelper";
import { communicationApi } from "../../api-helpers/CommunicationApiHelper";
import { ToasterObject } from "../schedule/interface-utils";
import {
  PatientGoals,
  PatientIntervention,
  PatientPgiMapResponse,
} from "grid-experience-client";
import { downloadCarePlanPdf } from "../../api-helpers/care-plan/CarePlanAPis";

interface ViewCarePlanProps {
  onHide: () => void;
  selectedCarePlan: CarePlanDto;
  reloadGrid?: () => void;
  reloadCarePlan?: (id: string) => void;
}

interface LoadingDataState {
  pgis: boolean;
  patientConditions: boolean;
  patientMedications: boolean;
  addendum: boolean;
  patientInfo: boolean;
  partnerName: boolean;
  internalCareTeam: boolean;
}

const ViewCarePlan = (props: ViewCarePlanProps) => {
  // UseState Variables
  const [workflowStates, setWorkflowStates] = useState<WorkflowState[] | []>(
    [],
  );
  const [selectedCarePlanDetails, setSelectedCarePlanDetails] = useState(
    {} as CarePlanDto,
  );
  const [addendumDetails, setAddendumDetails] = useState<
    CarePlanAddendumDto[] | []
  >([]);
  const [patientInfo, setPatientInfo] = useState({} as PatientPersonalInfo);
  const [patientConditions, setPatientConditions] =
    useState<GridDataResponse>();
  const [patientMedications, setPatientMedications] =
    useState<GridDataResponse>();
  const [patientPgis, setPatientPgis] = useState<PatientPgiMapResponse[] | []>(
    [],
  );
  const [openToaster, setOpenToaster] = useState<ToasterObject>({
    message: "",
    show: false,
    type: "",
  });
  const [coachAddendum, setCoachAddendum] = useState<string>("");
  const [mdAddendum, setMdAddendum] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState<LoadingDataState>({
    pgis: true,
    patientConditions: true,
    patientMedications: true,
    addendum: true,
    patientInfo: true,
    partnerName: true,
    internalCareTeam: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editContent, setEditContent] = useState(false);
  const [rejectModalShow, setRejectModalShow] = useState(false);
  const [contractInfo, setContractInfo] = useState<Contracts>({} as Contracts);
  const [internalCareTeam, setInternalCareTeam] =
    useState<InternalCareTeamMembers>();
  const [rejectionCategories, setRejectionCategories] = useState<Lookup[] | []>(
    [],
  );
  const [rejectionForm, setRejectionForm] = useState<UpdateCarePlanRequestDto>({
    rejectionTypeId: "",
    rejectionReason: "",
  });

  // APIs
  const carePlanApi = createCarePlanApi();
  const gridApi = createGridApi();
  const patientApi = createPatientApi();
  const lookupApi = createCommonUtilsApi();
  const pgiApi = createPgiApi();
  const workflowApi = createWorkflowApi();
  const commApi = communicationApi();

  const modules = {
    toolbar: [
      [
        { size: [] },
        "bold",
        "italic",
        "underline",
        { list: "ordered" },
        { list: "bullet" },
      ],
    ],
    clipboard: {
      // toggle to add extra line breaks when pasting HTML:
      matchVisual: false,
    },
  };

  const badgeStatus = {
    "Needs Action": "info",
    "In-Progress": "rescheduled",
    Done: "completed",
  };

  const getPatientPersonalInfo = () => {
    patientApi
      .patientsControllerGetPatientPersonalInfo(
        props?.selectedCarePlan?.patientId,
      )
      .then((response) => {
        setPatientInfo(response.data);
      })
      .catch((error) => {
        console.error("Error fetching patient personal info:", error);
      })
      .finally(() => {
        setLoadingData((prev) => ({ ...prev, patientInfo: false }));
      });
  };

  const getPatientContract = async () => {
    try {
      const response = await patientApi.patientsControllerGetPatientContracts(
        patientInfo.patientId,
      );
      if (response.data.length) {
        setContractInfo(response.data[0]);
      }
    } catch (error) {
      console.log("Error in loading patient contract ");
    } finally {
      setLoadingData((prev) => ({ ...prev, partnerName: false }));
    }
  };

  const getInternalCareTeam = async () => {
    try {
      const internalCareTeamResponse =
        await patientApi.patientsControllerGetInternalCareTeam(
          patientInfo.patientId,
        );
      const internalCareTeam = internalCareTeamResponse.data;
      setInternalCareTeam(internalCareTeam);
    } catch (error) {
      console.error("Error in fetching Internal Care Team");
    } finally {
      setLoadingData((prev) => ({ ...prev, internalCareTeam: false }));
    }
  };

  const getAddendumDetails = async () => {
    try {
      const response = await carePlanApi.carePlanControllerGetCarePlanAddedums(
        props.selectedCarePlan?.id,
      );
      setAddendumDetails(response.data);
      if (isReviewPending()) {
        setCoachAddendum("");
      } else {
        setCoachAddendum(response?.data[0]?.addendum || "");
      }
      setEditContent(false);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingData((prev) => ({ ...prev, addendum: false }));
    }
  };

  const getActiveConditionStatus = async (): Promise<string[]> => {
    let response,
      conditionStatus: Lookup[] = [];
    try {
      response = await lookupApi.lookupControllerFindAll(
        Lookups.CONDITION_STATUS,
      );
      conditionStatus = response.data.filter(
        (lookup: Lookup) => lookup.name?.toLowerCase() === "active",
      );
    } catch (error) {
      console.log(error);
    }
    return conditionStatus.length > 0 ? [conditionStatus[0].id] : [];
  };

  const getActiveMedicationStatus = async (): Promise<string[]> => {
    let response,
      medicationStatus: Lookup[] = [];
    try {
      response = await lookupApi.lookupControllerFindAll(
        Lookups.MEDICATION_STATUS,
      );
      medicationStatus = response.data.filter(
        (lookup: Lookup) => lookup.name?.toLowerCase() === "active",
      );
    } catch (error) {
      console.log(error);
    }
    return medicationStatus.length > 0 ? [medicationStatus[0].id] : [];
  };

  const getReviewedStatus = async (source: string): Promise<string[]> => {
    let response,
      reviewedStatus: Lookup[] = [];
    try {
      response = await lookupApi.lookupControllerFindAll(source);
      reviewedStatus = response.data.filter(
        (lookup: Lookup) => lookup.name?.toLowerCase() === "reviewed",
      );
    } catch (error) {
      console.log(error);
    }
    return reviewedStatus.length > 0 ? [reviewedStatus[0].id] : [];
  };

  const getStateId = (text: string): string => {
    const state =
      workflowStates.find((s) => s.stateName === text)?.stateId || "";
    return state ?? "";
  };

  const isReviewPending = (): boolean => {
    return selectedCarePlanDetails.status === "Review Pending" ? true : false;
  };

  const getWorkFlowStates = async (): Promise<WorkflowState[]> => {
    let states: WorkflowState[] = [];
    try {
      const statesResponse =
        await workflowApi.workflowControllerGetStatesForWorkflow(
          WorkflowConstants.carePlan,
        );
      states = [...statesResponse.data];
      setWorkflowStates(states);
    } catch (error) {
      console.log(error);
      return [] as WorkflowState[];
    }
    return states;
  };

  const getPatientConditions = async () => {
    try {
      const activeConditionId = await getActiveConditionStatus();
      const reviewedConditionId = await getReviewedStatus(
        Lookups.CONDITION_RECONCILIATION_STATUS,
      );

      const conditionsPayload = {
        filters: [
          {
            propertyName: "patientId",
            operator: "equals",
            propertyValue: props?.selectedCarePlan?.patientId,
          },
          {
            propertyName: "status",
            operator: "in",
            propertyValue: activeConditionId,
          },
          {
            propertyName: "conditionRecStatusId",
            operator: "in",
            propertyValue: reviewedConditionId,
          },
        ],
        sortCriteria: [],
        startRow: 0,
        endRow: 100,
      };

      const response =
        await patientApi.patientsControllerGetConditionGridData(
          conditionsPayload,
        );
      setPatientConditions(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingData((prev) => ({ ...prev, patientConditions: false }));
    }
  };

  const getPatientMedications = async () => {
    try {
      const activeMedicationId = await getActiveMedicationStatus();
      const reviewedMedicationId = await getReviewedStatus(
        Lookups.MEDICATION_RECONCILIATION_STATUS,
      );

      const request = {
        filters: [
          {
            propertyName: "patientId",
            operator: "equals",
            propertyValue: props?.selectedCarePlan?.patientId,
          },
          {
            propertyName: "statusId",
            operator: "in",
            propertyValue: activeMedicationId,
          },
          {
            propertyName: "medRecStatusId",
            operator: "in",
            propertyValue: reviewedMedicationId,
          },
        ],
        sortCriteria: [],
        startRow: 0,
        endRow: 100,
      };

      const response =
        await gridApi.gridControllerGetMedicationGridData(request);
      setPatientMedications(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingData((prev) => ({ ...prev, patientMedications: false }));
    }
  };

  const getPgis = async () => {
    try {
      const pgiResponse = await gridApi.gridControllerGetPatientPgiMap(
        props?.selectedCarePlan?.patientId,
      );
      const pgiData = pgiResponse.data;

      if (pgiData) {
        const structuredPGIs = pgiData?.filter(
          (pgi: PatientPgiMapResponse) => pgi.goal?.length > 0,
        );
        setPatientPgis(structuredPGIs);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingData((prev) => ({ ...prev, pgis: false }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    setRejectionForm({
      ...rejectionForm,
      [name]: value,
    });
  };

  const handleSave = async () => {
    if (
      addendumDetails?.length === 1 &&
      (addendumDetails?.[0].addendum === "" ||
        addendumDetails?.[0].addendum === coachAddendum)
    ) {
      return;
    }
    setIsSubmitting(true);
    try {
      if (addendumDetails?.length === 1) {
        await carePlanApi.carePlanControllerUpdateAddendum(
          { body: coachAddendum },
          addendumDetails[0]?.id,
        );
      } else {
        {
          await carePlanApi.carePlanControllerUpdateCarePlan(
            { addendum: coachAddendum },
            props.selectedCarePlan?.id,
          );
        }
      }

      getAddendumDetails();
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendForReview = async () => {
    // Save addendums
    handleSave();

    // Fetch Workflow States
    const reviewPendingStateId = getStateId("Review Pending");

    const requestBody: CarePlanReviewRequestDto = {
      designerStateId: reviewPendingStateId,
      designerId: props?.selectedCarePlan?.workflowId || "",
      conditions: { conditions: patientConditions },
      medications: { medications: patientMedications },
      pgi: { pgis: patientPgis },
    };

    // Update Care Plan Name and Send for Review
    setIsSubmitting(true);
    setLoading(true);
    try {
      await carePlanApi.carePlanControllerUpdateCarePlanName(
        props?.selectedCarePlan?.patientId,
      );
      await carePlanApi.carePlanControllerSentForReview(
        requestBody,
        props.selectedCarePlan?.id,
      );

      // Close Modal and reload Grid
      if (props.reloadGrid) {
        props.reloadGrid();
      }
      props.onHide();

      setEditContent(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignAndLock = async (isSigned: boolean) => {
    // Save addendums and update care Plan status to completed
    setIsSubmitting(true);
    try {
      await carePlanApi.carePlanControllerUpdateCarePlan(
        {
          addendum: mdAddendum,
          workFlowId: props?.selectedCarePlan?.workflowId!,
          workFlowStateId: getStateId("Completed"),
          isApproved: true,
          isSigned,
        },
        props.selectedCarePlan?.id,
      );

      // Close Modal and reload Grid
      if (props.reloadGrid) {
        props.reloadGrid();
      }
      props.onHide();
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRejectionCategories = async () => {
    try {
      const response = await lookupApi.lookupControllerFindAll(
        Lookups.CARE_PLAN_REJECTION_TYPES,
      );
      setRejectionCategories(
        response.data.sort((a: Lookup, b: Lookup) => a.sequence - b.sequence),
      );
    } catch (error) {
      console.log(error);
    }
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    const rejectedId = getStateId("Rejected");
    try {
      await carePlanApi.carePlanControllerUpdateCarePlan(
        {
          ...rejectionForm,
          workFlowStateId: rejectedId,
          workFlowId: selectedCarePlanDetails.workflowId || "",
          isRejected: true,
        },
        props.selectedCarePlan?.id,
      );

      if (props.reloadGrid) {
        props.reloadGrid();
      }
      setRejectModalShow(false);
      props.onHide();
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // const printCarePlanPDF = async () => {
  //   const printContent = document.querySelector(".custom-modal-container");
  //   if (printContent) {
  //     const newWindow = window.open("", "_blank");
  //     if (newWindow) {
  //       const fileName = `CarePlan_${patientInfo?.firstName}_${
  //         patientInfo?.lastName
  //       }_${formatDate(selectedCarePlanDetails?.reviewedOn)}`;

  //       newWindow.document.write(`
  //               <html>
  //                   <head>
  //                       <title>${fileName}</title>
  //                       <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.0.2/dist/css/bootstrap.min.css" rel="stylesheet">
  //                       <link href="../../assets/scss/custom/_carePlan.scss" rel="stylesheet">
  //                       <link href="../../assets/scss/custom/_custom.scss" rel="stylesheet">
  //                       <style>
  //                           @media print {
  //                               @page {
  //                                   margin: 1rem;
  //                                   size: auto;
  //                               }
  //                               body {
  //                                   margin: 1.6cm;
  //                                   margin-bottom: 2rem; /* Add bottom margin */
  //                               }
  //                           }
  //                       </style>
  //                   </head>
  //                   <body>
  //                       ${printContent.innerHTML}
  //                       <script>
  //                           window.onload = () => {
  //                               window.print();

  //                               // Set the document title for the print dialog
  //                               document.title = '${fileName}';

  //                               // Close the window after printing
  //                               window.close();
  //                           }
  //                       </script>
  //                   </body>
  //               </html>
  //           `);
  //       newWindow.document.close();
  //     }
  //   }
  // };

  const handleDownload = async () => {
    try {
      setLoading(true);
      const carePlanBucketName = process.env.REACT_APP_CARE_PLAN_BUCKET_NAME;
      if (!carePlanBucketName) {
        console.error("Care Plan Bucket Name is not defined");
        return;
      }
      let fileLocation = selectedCarePlanDetails.fileLocation;
      if (!fileLocation) {
        fileLocation = await downloadCarePlanPdf(props.selectedCarePlan.id);
      }
      const carePlanSignedURL = (
        await commApi.communicationControllerGetSignedUrlByBucketName({
          location: fileLocation!,
          bucketName: carePlanBucketName,
        })
      ).data;
      setLoading(false);

      window.open(carePlanSignedURL, "_blank");
    } catch (error) {
      console.error("Error downloading the PDF:", error);
    }
  };

  const loadAllData = () => {
    if (props.selectedCarePlan.isDraft) {
      setLoadingData({
        pgis: true,
        patientConditions: true,
        patientMedications: true,
        addendum: true,
        patientInfo: true,
        partnerName: true,
        internalCareTeam: true,
      });
      getPgis();
      getPatientConditions();
      getPatientMedications();
    } else {
      setLoadingData({
        pgis: false,
        patientConditions: false,
        patientMedications: false,
        addendum: true,
        patientInfo: true,
        partnerName: true,
        internalCareTeam: true,
      });
      setPatientPgis(props?.selectedCarePlan?.pgi?.pgis);
      setPatientConditions(props?.selectedCarePlan?.conditions?.conditions);
      setPatientMedications(props?.selectedCarePlan?.medications?.medications);
    }
    getWorkFlowStates();
    getAddendumDetails();
    getPatientPersonalInfo();
  };

  useEffect(() => {
    const interval = setTimeout(() => {
      setOpenToaster({ ...openToaster, show: false });
    }, 3000);
    return () => clearTimeout(interval);
  }, [openToaster]);

  useEffect(() => {
    loadAllData();
    setSelectedCarePlanDetails(props.selectedCarePlan);
  }, [props.selectedCarePlan]);

  useEffect(() => {
    if (patientInfo?.patientId) {
      getPatientContract();
      getInternalCareTeam();
    }
  }, [patientInfo]);

  useEffect(() => {
    const isFullyLoaded = Object.values(loadingData).every(
      (value) => value === false,
    );
    if (isFullyLoaded) {
      // All data is loaded
      setLoading(false);
    }
  }, [loadingData]);

  return (
    <>
      <div className="col-3">
        <Toast
          className={`toast toast-${
            openToaster.type === "success"
              ? "success"
              : openToaster.type === "error"
                ? "danger"
                : ""
          } top-right mt-10 move-in`}
          show={openToaster.show}
          onClose={() => setOpenToaster({ message: "", show: false, type: "" })}
        >
          <div className="ps-3">
            <span className="material-symbols-outlined fs-36">
              {openToaster.type === "success"
                ? "check_circle_outline"
                : openToaster.type === "error"
                  ? "error_outline"
                  : ""}
            </span>
          </div>
          <Toast.Body className="flex-fill">
            <div className="fs-14">
              {openToaster.type === "success"
                ? `${openToaster.message}`
                : openToaster.type === "error"
                  ? `${openToaster.message}`
                  : ""}
            </div>
          </Toast.Body>
          <Toast.Header></Toast.Header>
        </Toast>
      </div>
      {loading ? (
        <div className="d-flex justify-content-center my-3">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="px-8 modal-body custom-modal-height font-century-gothic">
            <main className="row g-8" id="view-care-plan-container">
              <div
                className={`custom-modal-container col-12 position-relative ${
                  editContent || isReviewPending() ? "col-md-9" : ""
                }`}
              >
                {selectedCarePlanDetails?.isRejected && (
                  <div className="rejected" role="alert">
                    <h6 className="mb-2 fs-16 text-black">
                      Rejected By {selectedCarePlanDetails?.reviewedBy?.name}
                    </h6>
                    <div className="gray-800">
                      {selectedCarePlanDetails?.rejectionReason}
                    </div>
                  </div>
                )}

                {/* Top Patient Info Bar */}
                <div className="text-light d-flex gap-1">
                  <span>
                    Patient Name:
                    {` ${patientInfo?.firstName} ${patientInfo?.lastName}`}
                  </span>
                  &#124;
                  <span>DOB: {formatDate(patientInfo?.dob)}</span> &#124;
                  <span>Wellinks ID: {patientInfo?.wellinksId}</span>
                </div>

                <img
                  src={logo}
                  className="min-w-5 position-absolute spire-logo"
                  style={{ right: "1rem" }}
                  alt="wellinks"
                  width={200}
                />

                {/* Personalized Care Plan  */}
                <h4 style={{ marginTop: "2rem" }}>Personalized Care Plan</h4>
                <div className="d-flex col-12 flex-wrap">
                  <div className="col-6">
                    {/* Patient Information */}
                    <h5>Patient Information</h5>
                    <div className="d-flex col-12 flex-wrap">
                      <span className="col-6">
                        <strong>Name:</strong> {patientInfo.firstName}{" "}
                        {patientInfo.lastName}
                      </span>
                      <span className="col-6">
                        <strong>DOB:</strong> {formatDate(patientInfo.dob)}
                      </span>
                      <span className="col-6">
                        <strong>Age:</strong> {getAge(patientInfo.dob)}
                      </span>
                      <span className="col-6">
                        <strong>Partner:</strong> {contractInfo.partnerName}
                      </span>
                    </div>
                  </div>

                  {/* Clinician Information */}
                  <div className="col-6 mt-2">
                    <div className="col-12">
                      <strong>Clinician:</strong>{" "}
                      {internalCareTeam?.primaryCoach?.name}
                    </div>
                    {selectedCarePlanDetails?.reviewedBy?.name && (
                      <div className="col-12">
                        <strong>Reviewed By:</strong>{" "}
                        {selectedCarePlanDetails?.reviewedBy?.name}
                      </div>
                    )}
                    {selectedCarePlanDetails?.reviewedOn && (
                      <div className="col-12">
                        {!selectedCarePlanDetails?.reviewedBy?.name ? (
                          <div>
                            <strong>Reviewed On:</strong>{" "}
                            {formatDateTime(
                              selectedCarePlanDetails?.reviewedOn,
                            ) || "-"}
                          </div>
                        ) : (
                          <>
                            <strong>Electronically Signed On:</strong>{" "}
                            {formatDateTime(
                              selectedCarePlanDetails?.reviewedOn,
                            ) || "-"}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <br />
                {
                  /* Patient Active Conditions */
                  patientConditions?.total ? (
                    <div>
                      <h4 className="mt-4">Active Conditions</h4>
                      <div className="bg-white rounded p-0 fs-14">
                        <div className="d-flex bg-teal-light">
                          <strong
                            className="p-2 border-end"
                            style={{ width: "10%" }}
                          >
                            Code
                          </strong>
                          <strong className="p-2">Condition</strong>
                        </div>
                        {patientConditions?.data?.map(
                          (condition: any, index: number) => (
                            <div
                              key={index}
                              className={`d-flex d-block border-bottom ${index % 2 === 1 ? "bg-light-subtle" : ""}`}
                            >
                              <span
                                className="p-2 border-end"
                                style={{ width: "10%" }}
                              >
                                {condition.conditionName.split(":")[0]}
                              </span>
                              <span className="p-2">
                                {condition.conditionName.split(":")[1]}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  ) : null
                }

                <br />

                {
                  /* Patient Active Medications */
                  patientMedications?.total ? (
                    <div>
                      <h4 className="mt-4">Active Medications</h4>
                      <div className="bg-white rounded p-0 fs-14">
                        <div className="d-flex bg-teal-light">
                          <strong
                            className="p-2 border-end"
                            style={{ width: "30%" }}
                          >
                            Name
                          </strong>
                          <strong className="p-2">Dosage</strong>
                        </div>
                        {patientMedications?.data?.map(
                          (medication: any, index: number) => (
                            <div
                              key={index}
                              className={`d-flex d-block border-bottom ${index % 2 === 1 ? "bg-light-subtle" : ""}`}
                            >
                              <span
                                className="p-2 border-end"
                                style={{ width: "30%" }}
                              >
                                {medication.medicationName}
                              </span>
                              <span className="p-2">
                                {medication.dosageRecommended}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  ) : null
                }

                <br />

                {
                  /* Patient Active Problems, Goals and Interventions */
                  patientPgis?.length ? (
                    <div>
                      <h4 className="mt-4">
                        Active Problems, Goals & Interventions
                      </h4>
                      <div className="bg-white rounded p-0 fs-14">
                        {patientPgis?.map(
                          (
                            pgi: PatientPgiMapResponse,
                            problemIndex: number,
                          ) => (
                            <>
                              <span className="fs-15 mb-2">
                                {problemIndex + 1} {". "}
                                <strong>PROBLEM: </strong>
                                {pgi?.problem?.problem}
                              </span>

                              {pgi.goal?.map(
                                (pgiGoal: PatientGoals, goalIndex: number) => (
                                  <>
                                    <div
                                      key={goalIndex}
                                      className="d-flex d-block border-bottom bg-teal-light"
                                    >
                                      <span
                                        className="border-end p-3"
                                        style={{ width: "10rem" }}
                                      >
                                        {`Goal ${goalIndex + 1}`}
                                      </span>
                                      <div className="w-90 d-flex justify-content-between p-3">
                                        <span>{pgiGoal.goal?.goal} </span>
                                        <span
                                          className={`h-fit-content badge background-${
                                            badgeStatus[
                                              pgiGoal.status as keyof typeof badgeStatus
                                            ]
                                          }`}
                                        >
                                          {pgiGoal.status}
                                        </span>
                                      </div>
                                    </div>

                                    {pgiGoal.interventions?.map(
                                      (
                                        pgiIntervention: PatientIntervention,
                                        interventionIndex: number,
                                      ) => (
                                        <div
                                          key={interventionIndex}
                                          className="d-flex d-block border-bottom"
                                        >
                                          <span
                                            className="border-end p-3"
                                            style={{ width: "10rem" }}
                                          >
                                            {" "}
                                            {`Intervention ${interventionIndex + 1}`}
                                          </span>
                                          <div className="w-90 d-flex justify-content-between p-3">
                                            <span>
                                              {
                                                pgiIntervention.intervention
                                                  ?.intervention
                                              }
                                            </span>
                                            <span
                                              className={`h-fit-content badge background-${
                                                badgeStatus[
                                                  pgiIntervention.status as keyof typeof badgeStatus
                                                ]
                                              }`}
                                            >
                                              {pgiIntervention.status}
                                            </span>
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </>
                                ),
                              )}

                              <br />
                            </>
                          ),
                        )}
                      </div>
                    </div>
                  ) : null
                }

                <br />

                {
                  // Addendums
                  addendumDetails.length ? (
                    <div className="mt-4">
                      <h5 className="text-decoration-underline mt-6">
                        Addendum
                      </h5>
                      {addendumDetails.map((addendum, index) => (
                        <div className="ms-4 text-dark fs-13 pb-2" key={index}>
                          <div className="mt-3">
                            <div className="d-flex gap-2 justify-content-between">
                              <h6 className="fs-15">
                                By Coach{" "}
                                <span className="fw-normal">
                                  {"("}
                                  {addendum?.writtenByUser?.name || ""}
                                  {")"}
                                </span>
                              </h6>
                              <div>{formatDate(addendum.createdAt)}</div>
                            </div>
                          </div>
                          <ReactQuill
                            value={addendum.addendum || ""}
                            readOnly={true}
                            theme="bubble"
                            modules={{ toolbar: false }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : null
                }
              </div>
              {(editContent || isReviewPending()) && (
                <div className="col-12 col-md-3 border-start pe-0 custom-modal-container overflow-auto">
                  <h5>Addendum</h5>
                  <ReactQuill
                    theme="snow"
                    placeholder="Write notes here..."
                    style={{ height: `calc(100% - 2rem)` }}
                    formats={[
                      "header",
                      "font",
                      "size",
                      "bold",
                      "italic",
                      "underline",
                      "strike",
                      "blockquote",
                      "list",
                      "bullet",
                      "indent",
                    ]}
                    modules={modules}
                    value={isReviewPending() ? mdAddendum : coachAddendum}
                    onChange={(value) => {
                      isReviewPending()
                        ? setMdAddendum(value)
                        : setCoachAddendum(value);
                    }}
                  />
                </div>
              )}
            </main>
          </div>
          {/* Render buttons only if status is not Completed */}
          {selectedCarePlanDetails?.status !== "Completed" && (
            <div className="px-6 modal-footer text-end w-100 border-top d-flex justify-content-between border-top">
              {/* Left Side Buttons */}
              <div>
                {/* Display Discard button in Edit Mode */}
                {editContent ? (
                  <button
                    className="btn btn-sm btn-outline-primary rounded-pill px-5"
                    type="submit"
                    onClick={() => {
                      props.onHide();
                      setEditContent(false);
                    }}
                  >
                    Discard
                  </button>
                ) : null}

                {/* Display Edit button if status is Draft */}
                {selectedCarePlanDetails?.isDraft && !editContent && (
                  <button
                    className="btn btn-sm btn-outline-primary rounded-pill px-5"
                    type="submit"
                    onClick={() => setEditContent(true)}
                  >
                    Edit
                  </button>
                )}

                {/* Display Reject button if status is Review Pending */}
                {isReviewPending() && (
                  <button
                    className="btn btn-sm btn-outline-primary rounded-pill px-5"
                    type="submit"
                    onClick={() => {
                      getRejectionCategories();
                      setRejectModalShow(true);
                    }}
                  >
                    Reject
                  </button>
                )}
              </div>

              {/* Right Side Buttons */}
              <div>
                {/* Display Save button in Edit Mode */}
                {editContent && (
                  <button
                    className="btn btn-sm btn-outline-primary rounded-pill px-5 me-2"
                    type="submit"
                    disabled={isSubmitting}
                    onClick={handleSave}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                )}

                {/* Display Send for Review button if status is Draft */}
                {selectedCarePlanDetails?.isDraft && (
                  <button
                    className="btn btn-sm btn-primary rounded-pill px-5"
                    type="submit"
                    disabled={isSubmitting}
                    onClick={handleSendForReview}
                  >
                    Save & Send for Review
                  </button>
                )}

                {isReviewPending() &&
                  contractInfo?.billingModel ===
                    BILLING_MODELS.VALUE_BASED_CARE && (
                    <button
                      className="btn btn-sm btn-primary rounded-pill px-5 mx-2"
                      type="submit"
                      disabled={isSubmitting}
                      onClick={() => handleSignAndLock(false)}
                      title="Lock the care plan without signing.  Only available for value based care contracts."
                    >
                      Lock
                    </button>
                  )}

                {/* Display Sign & Lock button if status is Review Pending */}
                {isReviewPending() && (
                  <button
                    className="btn btn-sm btn-primary rounded-pill px-5"
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => handleSignAndLock(true)}
                  >
                    Sign & Lock
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Download Button if Care Plan is completed */}
          {selectedCarePlanDetails?.status === "Completed" && (
            <div className="px-6 modal-footer text-end w-100 border-top d-flex justify-content-end border-top">
              <div>
                <button
                  className="btn btn-sm btn-primary rounded-pill px-5"
                  type="submit"
                  onClick={handleDownload}
                >
                  Download
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal for Rejecting Care Plan */}
      <Modal
        show={rejectModalShow}
        onHide={() => setRejectModalShow(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <>
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              Reason for Rejection
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2">
              <label
                htmlFor="rejectionTypeId"
                className="form-label fw-semibold mb-1"
              >
                Category
              </label>
              <select
                className="form-select form-select-sm"
                id="rejectionTypeId"
                value={rejectionForm.rejectionTypeId}
                name="rejectionTypeId"
                onChange={handleInputChange}
                required
              >
                <option value={""} disabled>
                  Select category
                </option>
                {rejectionCategories?.length &&
                  rejectionCategories?.map((item: Lookup, index: number) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="col-12 mb-3">
              <label
                htmlFor="rejectionReason"
                className="form-label fw-semibold mb-1"
              >
                Comments
              </label>
              <textarea
                name="rejectionReason"
                value={rejectionForm.rejectionReason}
                onChange={handleInputChange}
                className="form-control form-control-sm"
                id="rejectionReason"
                placeholder="Add Comments..."
                rows={6}
                required
              ></textarea>
            </div>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary rounded-pill px-6 my-0"
              onClick={() => {
                setRejectModalShow(false);
              }}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary rounded-pill px-6 my-0"
              onClick={handleReject}
              disabled={rejectionForm.rejectionTypeId === ""}
            >
              Reject
            </button>
          </Modal.Footer>
        </>
      </Modal>
    </>
  );
};

export default ViewCarePlan;
